import {defineStore} from 'pinia'
import {ref, watch} from 'vue'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {
  NotificationFilterType,
} from '@toolify/server/src/controllers/NotificationController/enum/NotificationFilterType'
import {NotificationType} from '@toolify/server/src/models/mongoose/NotificationModel/enum/NotificationType'
import {
  INotificationResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/INotificationResponseInstance'
import {GlobalSocketEventType} from '@toolify/server/src/services/SocketService/enum/GlobalSocketEventType'
import {INotificationsSeenEmitPayload} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/INotificationsSeenEmitPayload'
import {INotificationsReadEmitPayload} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/INotificationsReadEmitPayload'
import {INotificationUpdatedEmitPayload} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/INotificationUpdatedEmitPayload'
import {useNotificationApiStore} from '@toolify/client/src/stores/api/NotificationApiStore/useNotificationApiStore'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {useSocketListener} from '@toolify/client/src/composables/useSocketListener'
import {IUnreadNotificationsTypesCountMap} from '@toolify/client/src/modules/notifications/types/IUnreadNotificationsTypesCountMap'
import {
  NotificationTypeStrategyFactory,
} from '@toolify/client/src/stores/NotificationStore/factories/NotificationTypeStrategyFactory'
import {mapNotificationResponseInstance} from '@toolify/client/src/modules/api/singletons/mapNotificationResponseInstance'
import {IToJson} from '@toolify/client/src/types/IToJson'
import {
  doesNotificationPayloadScopeMatchScope,
} from '@toolify/client/src/modules/notifications/functions/doesNotificationPayloadScopeMatchScope'
import {
  doesNotificationResourcePathMatchScope,
} from '@toolify/client/src/modules/notifications/functions/doesNotificationResourcePathMatchScope'
import {
  isNotificationShownToListViewer,
} from '@toolify/client/src/modules/notifications/functions/isNotificationShownToListViewer'
import {INotificationListViewer} from '@toolify/client/src/modules/notifications/types/INotificationListViewer'
import {NotificationSeenIdsLimit} from '@toolify/server/src/services/NotificationService/constants/NotificationSeenIdsLimit'

export const GLOBAL_NOTIFICATION_COUNTS_SCOPE_KEY = 'global'
const REFETCH_DEBOUNCE_MS = 400
const SEEN_FLUSH_DEBOUNCE_MS = 500
const LIVE_SEEN_RETENTION_MS = 60000

interface IScopeRegistration {
  refCount: number
  resourceType: ResourceType | null
  resourceId: string | null
}

function createEmptyCounts(): IUnreadNotificationsTypesCountMap {
  return {
    [NotificationFilterType.All]: 0,
    [NotificationFilterType.Unread]: 0,
    [NotificationFilterType.Mentions]: 0,
    [NotificationFilterType.AssignedToMe]: 0,
    [NotificationFilterType.Unseen]: 0,
  }
}

// Single owner of the unread/unseen notification counters. Bell instances register a scope
// (global or one resource) instead of each fetching and mutating their own copy, so N mounted
// bells cost one request per scope and one socket-driven mutation per event.
export const useNotificationCountsStore = defineStore('notificationCounts', () => {
  const notificationApiStore = useNotificationApiStore()
  const authStore = useAuthStore()

  const countsByScopeKey = ref(new Map<string, IUnreadNotificationsTypesCountMap>())
  const scopeRegistrations = new Map<string, IScopeRegistration>()
  const inflightByScopeKey = new Map<string, Promise<void>>()
  const refetchTimerByScopeKey = new Map<string, ReturnType<typeof setTimeout>>()
  // Bumped by every local mutation (socket delta, account switch): a fetch whose snapshot predates
  // the bump is stale and must be discarded instead of overwriting fresher local state
  const generationByScopeKey = new Map<string, number>()
  const refetchQueuedScopeKeys = new Set<string>()
  const listViewerByKey = new Map<symbol, INotificationListViewer>()
  const pendingSeenNotificationIds = new Set<string>()
  const liveSeenAtByNotificationId = new Map<string, number>()
  let seenFlushTimer: ReturnType<typeof setTimeout> | null = null

  function bumpGeneration(scopeKey: string) {
    generationByScopeKey.set(scopeKey, (generationByScopeKey.get(scopeKey) ?? 0) + 1)
  }

  function getScopeKey(resourceType?: ResourceType | null, resourceId?: string | null): string {
    return resourceType && resourceId ? `${resourceType}:${resourceId}` : GLOBAL_NOTIFICATION_COUNTS_SCOPE_KEY
  }

  function getCounts(scopeKey: string): IUnreadNotificationsTypesCountMap {
    return countsByScopeKey.value.get(scopeKey) ?? createEmptyCounts()
  }

  function getUnseenCount(scopeKey: string): number {
    return getCounts(scopeKey)[NotificationFilterType.Unseen] || 0
  }

  function setCounts(scopeKey: string, counts: IUnreadNotificationsTypesCountMap) {
    const newMap = new Map(countsByScopeKey.value)
    newMap.set(scopeKey, counts)
    countsByScopeKey.value = newMap
  }

  function registerScope(resourceType?: ResourceType | null, resourceId?: string | null): string {
    const scopeKey = getScopeKey(resourceType, resourceId)
    const registration = scopeRegistrations.get(scopeKey)
    if(registration) {
      registration.refCount++
      return scopeKey
    }
    scopeRegistrations.set(scopeKey, {
      refCount: 1,
      resourceType: resourceType ?? null,
      resourceId: resourceId ?? null,
    })
    if(authStore.currentEntity?.entityId) {
      fetchCounts(scopeKey)
    }
    return scopeKey
  }

  function unregisterScope(scopeKey: string) {
    const registration = scopeRegistrations.get(scopeKey)
    if(!registration) {
      return
    }
    registration.refCount--
    if(registration.refCount > 0) {
      return
    }
    scopeRegistrations.delete(scopeKey)
    const timer = refetchTimerByScopeKey.get(scopeKey)
    if(timer) {
      clearTimeout(timer)
      refetchTimerByScopeKey.delete(scopeKey)
    }
  }

  function fetchCounts(scopeKey: string): Promise<void> {
    const registration = scopeRegistrations.get(scopeKey)
    // A refetch queued behind an in-flight request can fire after the scope unregisters; without
    // this bail it would degrade into a GLOBAL fetch stored under the resource scope's key
    if(!registration) {
      return Promise.resolve()
    }
    const inflight = inflightByScopeKey.get(scopeKey)
    if(inflight) {
      // A fetch requested while one is running must not be silently absorbed — the running
      // request's snapshot predates whatever triggered this call, so run one more afterwards
      refetchQueuedScopeKeys.add(scopeKey)
      return inflight
    }
    const generationAtRequest = generationByScopeKey.get(scopeKey) ?? 0
    const promise = (async () => {
      try {
        const response = await notificationApiStore.getUnreadNotificationTypesCount(
          registration?.resourceType ?? null,
          registration?.resourceId ?? null,
        )
        if((generationByScopeKey.get(scopeKey) ?? 0) !== generationAtRequest) {
          // A local mutation landed mid-flight (socket delta, account switch): this server
          // snapshot is older than the local state — drop it and fetch a fresh one
          refetchQueuedScopeKeys.add(scopeKey)
          return
        }
        setCounts(scopeKey, {
          [NotificationFilterType.All]: 0,
          [NotificationFilterType.Unread]: response[NotificationFilterType.Unread] || 0,
          [NotificationFilterType.Mentions]: response[NotificationFilterType.Mentions] || 0,
          [NotificationFilterType.AssignedToMe]: response[NotificationFilterType.AssignedToMe] || 0,
          [NotificationFilterType.Unseen]: response[NotificationFilterType.Unseen] || 0,
        })
      } catch {
        // Counts stay stale until the next socket-triggered refetch — better than an unhandled
        // rejection from a fire-and-forget call site
      } finally {
        if(inflightByScopeKey.get(scopeKey) === promise) {
          inflightByScopeKey.delete(scopeKey)
        }
        if(refetchQueuedScopeKeys.delete(scopeKey)) {
          fetchCounts(scopeKey)
        }
      }
    })()
    inflightByScopeKey.set(scopeKey, promise)
    return promise
  }

  function scheduleRefetch(scopeKey: string) {
    if(!scopeRegistrations.has(scopeKey)) {
      return
    }
    const existingTimer = refetchTimerByScopeKey.get(scopeKey)
    if(existingTimer) {
      clearTimeout(existingTimer)
    }
    refetchTimerByScopeKey.set(scopeKey, setTimeout(() => {
      refetchTimerByScopeKey.delete(scopeKey)
      fetchCounts(scopeKey)
    }, REFETCH_DEBOUNCE_MS))
  }

  function zeroUnseen(scopeKey: string) {
    bumpGeneration(scopeKey)
    setCounts(scopeKey, {
      ...getCounts(scopeKey),
      [NotificationFilterType.Unseen]: 0,
    })
  }

  function setListViewer(viewerKey: symbol, viewer: INotificationListViewer | null) {
    if(viewer) {
      listViewerByKey.set(viewerKey, viewer)
      return
    }
    listViewerByKey.delete(viewerKey)
  }

  function isNotificationSeenLive(notification: INotificationResponseInstance<NotificationType>): boolean {
    for(const viewer of listViewerByKey.values()) {
      if(isNotificationShownToListViewer(notification, viewer)) {
        return true
      }
    }
    return false
  }

  function wasNotificationSeenLive(notificationId: string): boolean {
    return liveSeenAtByNotificationId.has(notificationId)
  }

  function rememberLiveSeenNotification(notificationId: string) {
    const now = Date.now()
    for(const [id, seenAt] of liveSeenAtByNotificationId) {
      if(now - seenAt > LIVE_SEEN_RETENTION_MS) {
        liveSeenAtByNotificationId.delete(id)
      }
    }
    liveSeenAtByNotificationId.set(notificationId, now)
  }

  function queueSeenNotificationIds(notificationIds: string[]) {
    notificationIds.forEach(notificationId => pendingSeenNotificationIds.add(notificationId))
    if(seenFlushTimer) {
      return
    }
    seenFlushTimer = setTimeout(() => {
      seenFlushTimer = null
      flushSeenNotifications()
    }, SEEN_FLUSH_DEBOUNCE_MS)
  }

  async function flushSeenNotifications() {
    if(seenFlushTimer) {
      clearTimeout(seenFlushTimer)
      seenFlushTimer = null
    }
    if(!pendingSeenNotificationIds.size) {
      return
    }
    const notificationIds = [...pendingSeenNotificationIds]
    pendingSeenNotificationIds.clear()
    try {
      for(let index = 0; index < notificationIds.length; index += NotificationSeenIdsLimit) {
        await notificationApiStore.setUserNotificationsSeen(notificationIds.slice(index, index + NotificationSeenIdsLimit))
      }
    } catch {
      for(const scopeKey of scopeRegistrations.keys()) {
        scheduleRefetch(scopeKey)
      }
    }
  }

  function markNotificationsSeen(notifications: INotificationResponseInstance<NotificationType>[]) {
    const countedNotifications = notifications.filter(notification => {
      return !pendingSeenNotificationIds.has(notification.id) && !wasNotificationSeenLive(notification.id)
    })
    for(const [scopeKey, registration] of scopeRegistrations) {
      const seenCount = countedNotifications.filter(notification => {
        return doesNotificationResourcePathMatchScope(notification.resourcePath, registration.resourceType, registration.resourceId)
      }).length
      if(!seenCount) {
        continue
      }
      const counts = getCounts(scopeKey)
      bumpGeneration(scopeKey)
      setCounts(scopeKey, {
        ...counts,
        [NotificationFilterType.Unseen]: Math.max(0, (counts[NotificationFilterType.Unseen] || 0) - seenCount),
      })
    }
    queueSeenNotificationIds(notifications.map(notification => notification.id))
  }

  function getAffectedFilterTypes(mappedNotification: INotificationResponseInstance<NotificationType>): NotificationFilterType[] {
    try {
      // The factory throws for unknown types (e.g. a newer server during a rolling deploy) — a
      // throw here would also kill every later NotificationCreated listener on the socket
      const notificationTypeStrategy = new NotificationTypeStrategyFactory().getStrategy(mappedNotification.type)
      return notificationTypeStrategy?.getAffectedFilterTypes(mappedNotification)
        ?? [NotificationFilterType.Unread]
    } catch {
      return [NotificationFilterType.Unread]
    }
  }

  function onNotificationCreated({notification}: {notification: INotificationResponseInstance<NotificationType>}) {
    const mappedNotification = mapNotificationResponseInstance(notification as unknown as IToJson<INotificationResponseInstance<NotificationType>>)
    if(mappedNotification.isTransient || mappedNotification.readAt) {
      return
    }
    const isSeenLive = isNotificationSeenLive(mappedNotification)
    if(isSeenLive) {
      rememberLiveSeenNotification(mappedNotification.id)
      queueSeenNotificationIds([mappedNotification.id])
    }
    // A count above 1 means the event was folded into an already-unread group: the unread-family
    // counters must not move, but the server also reset the group's seenAt, so the Unseen badge may
    // have changed in a way the client cannot derive locally — refetch instead
    const isNewUnreadEntry = (mappedNotification.unreadGroupCount ?? 1) <= 1
    if(!isNewUnreadEntry) {
      if(isSeenLive) {
        return
      }
      for(const [scopeKey, registration] of scopeRegistrations) {
        if(doesNotificationResourcePathMatchScope(mappedNotification.resourcePath, registration.resourceType, registration.resourceId)) {
          scheduleRefetch(scopeKey)
        }
      }
      return
    }
    const affectedFilterTypes = getAffectedFilterTypes(mappedNotification)
      .filter(affectedFilterType => affectedFilterType !== NotificationFilterType.All)
    for(const [scopeKey, registration] of scopeRegistrations) {
      if(!doesNotificationResourcePathMatchScope(mappedNotification.resourcePath, registration.resourceType, registration.resourceId)) {
        continue
      }
      const nextCounts = {...getCounts(scopeKey)}
      for(const affectedFilterType of affectedFilterTypes) {
        nextCounts[affectedFilterType] = (nextCounts[affectedFilterType] || 0) + 1
      }
      if(!isSeenLive) {
        nextCounts[NotificationFilterType.Unseen] = (nextCounts[NotificationFilterType.Unseen] || 0) + 1
      }
      bumpGeneration(scopeKey)
      setCounts(scopeKey, nextCounts)
    }
  }

  function onNotificationsSeen(payload: INotificationsSeenEmitPayload) {
    if(payload?.notifications) {
      for(const [scopeKey, registration] of scopeRegistrations) {
        const isScopeAffected = payload.notifications.some(notification => {
          return doesNotificationResourcePathMatchScope(notification.resourcePath, registration.resourceType, registration.resourceId)
        })
        if(isScopeAffected) {
          scheduleRefetch(scopeKey)
        }
      }
      return
    }
    const isGlobalPayload = !payload?.resourceType && !payload?.resourceId
    for(const [scopeKey, registration] of scopeRegistrations) {
      if(!doesNotificationPayloadScopeMatchScope(registration.resourceType, registration.resourceId, payload?.resourceType, payload?.resourceId)) {
        continue
      }
      const nextCounts = {...getCounts(scopeKey)}
      if(registration.resourceType || isGlobalPayload) {
        // mark-all-seen (global, or scoped to this resource) clears the whole scope
        nextCounts[NotificationFilterType.Unseen] = 0
      } else {
        // global scope, resource-scoped action: subtract what that resource had pending
        nextCounts[NotificationFilterType.Unseen] = Math.max(0, (nextCounts[NotificationFilterType.Unseen] || 0) - (payload.count ?? 0))
      }
      bumpGeneration(scopeKey)
      setCounts(scopeKey, nextCounts)
    }
  }

  function onNotificationsRead(payload: INotificationsReadEmitPayload) {
    for(const [scopeKey, registration] of scopeRegistrations) {
      if(!doesNotificationPayloadScopeMatchScope(registration.resourceType, registration.resourceId, payload?.resourceType, payload?.resourceId)) {
        continue
      }
      scheduleRefetch(scopeKey)
    }
  }

  function onNotificationUpdated({notification}: INotificationUpdatedEmitPayload) {
    for(const [scopeKey, registration] of scopeRegistrations) {
      if(!doesNotificationResourcePathMatchScope(notification.resourcePath, registration.resourceType, registration.resourceId)) {
        continue
      }
      scheduleRefetch(scopeKey)
    }
  }

  // The store lives for the whole app, so the listeners must not ride the lifecycle of whichever
  // component happens to instantiate it first — useLifecycleHooks: false registers nothing on its
  // own, hence the explicit start()
  useSocketListener(GlobalSocketEventType.NotificationCreated, onNotificationCreated, {useLifecycleHooks: false}).start()
  useSocketListener(GlobalSocketEventType.NotificationsSeen, onNotificationsSeen, {useLifecycleHooks: false}).start()
  useSocketListener(GlobalSocketEventType.NotificationsRead, onNotificationsRead, {useLifecycleHooks: false}).start()
  useSocketListener(GlobalSocketEventType.NotificationUpdated, onNotificationUpdated, {useLifecycleHooks: false}).start()
  useSocketListener(GlobalSocketEventType.NotificationGroupUpdated, onNotificationUpdated, {useLifecycleHooks: false}).start()

  watch(() => {
    return authStore.currentEntity?.entityId
  }, (value, oldValue) => {
    if(value === oldValue) {
      return
    }
    countsByScopeKey.value = new Map()
    // Invalidate whatever the previous account still has in flight and cancel pending refetches —
    // without this, the old account's resolving request would populate the new account's badges
    for(const scopeKey of scopeRegistrations.keys()) {
      bumpGeneration(scopeKey)
    }
    for(const timer of refetchTimerByScopeKey.values()) {
      clearTimeout(timer)
    }
    refetchTimerByScopeKey.clear()
    if(seenFlushTimer) {
      clearTimeout(seenFlushTimer)
      seenFlushTimer = null
    }
    pendingSeenNotificationIds.clear()
    liveSeenAtByNotificationId.clear()
    if(!value) {
      return
    }
    for(const scopeKey of scopeRegistrations.keys()) {
      fetchCounts(scopeKey)
    }
  })

  return {
    getScopeKey,
    getCounts,
    getUnseenCount,
    registerScope,
    unregisterScope,
    zeroUnseen,
    setListViewer,
    isNotificationSeenLive,
    wasNotificationSeenLive,
    markNotificationsSeen,
    flushSeenNotifications,
  }
})
